import './ListStores.css';
import '../sharedOpportunity/SharedOpportunity.css';
import '../yourLists/YourLists.css';
import '../../components/AllSites/AllSites.css';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HiOutlineOfficeBuilding, HiChevronRight, HiX, HiOutlineGlobe, HiOutlinePencilAlt } from 'react-icons/hi';
import { HiOutlineListBullet, HiOutlineMapPin, HiOutlineMagnifyingGlass, HiOutlineShare, HiOutlineTrash, HiOutlineChatBubbleLeftRight } from 'react-icons/hi2';
import { HiOutlineSun } from 'react-icons/hi';
import { BsSnow } from 'react-icons/bs';
import { fetchListStoresFull, fetchListById, removeStoresFromList, searchUsers, shareList, fetchSharedUsers, unshareList, updateSharePermission, updateOrgAccess, fetchListComments, ShareUser, SharedEntry } from '../../services/listService';
import { useAuthStore } from '../../stores';
import { calculateStoreRecommendations } from '../../utils/recommendationLogic';
import { exportOpportunitiesByRegionData } from '../../utils/excelExportUtils';
import { GoogleMapView, MapStore } from '../../components/GoogleMapView';
import HeatmapToggle from '../../components/HeatmapToggle/HeatmapToggle';
import StreetViewPanel from '../opportunityByRegion/StreetViewPanel';
import Spinner from '../../components/Spinner/Spinner';
import CustomDropdown from '../../components/CustomDropdown/CustomDropdown';
import StoreDetailModal, { StoreDetailInfo } from '../../components/StoreDetailModal/StoreDetailModal';
import BrandNotesDialog from '../../components/BrandsTable/BrandNotesDialog';
import { fetchStoreNotes } from '../../services/brandSitesService';
import toast from 'react-hot-toast';
import ListComments from './ListComments';
import Tooltip from '../../components/Tooltip/Tooltip';
import { getTooltipText } from '../../utils/tooltip';

type FilterType = 'any-opportunity' | 'purchek' | 'containment' | 'cart-manager' | 'subscription' | 'paired-wheel' | 'high-crime' | 'critical-crime' | 'cart-ordinance' | 'extreme-weather';

function capitalizeWords(str: string): string {
  if (!str) return str;
  return str.replace(/\b\w/g, c => c.toUpperCase());
}

function ListStores() {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();

  const [listName, setListName] = useState('');
  const [stores, setStores] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [streetViewStore, setStreetViewStore] = useState<{ lat: number; lng: number; name?: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [selectedStoreIds, setSelectedStoreIds] = useState<Set<string>>(new Set());
  const [activeFilters, setActiveFilters] = useState<Set<FilterType>>(new Set<FilterType>());
  const [expandedSolutions, setExpandedSolutions] = useState<Set<string>>(new Set());
  const [expandedWhitespace, setExpandedWhitespace] = useState<Set<string>>(new Set());

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareSearch, setShareSearch] = useState('');
  const [shareSuggestions, setShareSuggestions] = useState<ShareUser[]>([]);
  const [sharedWith, setSharedWith] = useState<SharedEntry[]>([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [openCommentStoreId, setOpenCommentStoreId] = useState<string | null>(null);
  const [storeCommentCounts, setStoreCommentCounts] = useState<Map<string, number>>(new Map());
  const [shareDefaultPermission, setShareDefaultPermission] = useState<'view' | 'edit'>('view');
  const [shareOrgAccess, setShareOrgAccess] = useState<'view' | 'edit' | null>(null);
  const [canEdit, setCanEdit] = useState(true);
  const [storeDetailModalOpen, setStoreDetailModalOpen] = useState(false);
  const [selectedStoreDetail, setSelectedStoreDetail] = useState<StoreDetailInfo | null>(null);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedStoreForNotes, setSelectedStoreForNotes] = useState('');
  const [selectedStorePoiId, setSelectedStorePoiId] = useState('');

  const handleOpenShare = () => {
    setShowShareModal(true);
    setShareSearch('');
    setShareSuggestions([]);
    setShareDefaultPermission('view');
    setShareLoading(true);
    fetchSharedUsers(listName).then((entries: any) => {
      setSharedWith(entries);
      setShareLoading(false);
    });
  };

  const handleShareSearchChange = async (value: string) => {
    setShareSearch(value);
    if (value.trim().length < 2) { setShareSuggestions([]); return; }
    setSearchLoading(true);
    const users = await searchUsers(value.trim());
    const alreadySharedIds = new Set(sharedWith.map(s => s.shared_with));
    setShareSuggestions(users.filter((u: any) => !alreadySharedIds.has(u.id)));
    setSearchLoading(false);
  };

  const handleShareWithUser = async (targetUser: ShareUser) => {
    const result = await shareList(listName, targetUser.id, shareDefaultPermission);
    if (result.success) {
      toast.success(`Shared with ${targetUser.name || targetUser.email}`);
      setShareSearch('');
      setShareSuggestions([]);
      const entries = await fetchSharedUsers(listName);
      setSharedWith(entries);
    } else {
      toast.error(result.error || 'Failed to share');
    }
  };

  const handlePermissionChange = async (shareId: string, permission: 'view' | 'edit') => {
    const result = await updateSharePermission(shareId, permission);
    if (result.success) {
      setSharedWith(prev => prev.map(s => s.id === shareId ? { ...s, permission } : s));
    } else {
      toast.error(result.error || 'Failed to update permission');
    }
  };

  const handleOrgAccessChange = async (value: 'view' | 'edit' | null) => {
    const result = await updateOrgAccess(listName, value);
    if (result.success) {
      setShareOrgAccess(value);
      toast.success(value ? `Organization access set to ${value}` : 'Organization access removed');
    } else {
      toast.error(result.error || 'Failed to update');
    }
  };

  const handleUnshare = async (shareId: string) => {
    const result = await unshareList(shareId);
    if (result.success) {
      setSharedWith(prev => prev.filter(s => s.id !== shareId));
      toast.success('Access removed');
    } else {
      toast.error(result.error || 'Failed to remove');
    }
  };

  const handleDownloadSelected = () => {
    if (selectedStoreIds.size === 0) return;
    const selected = filteredStores.filter((s: any) => selectedStoreIds.has(s.id));
    if (selected.length === 0) return;
    const exportStores = selected.map((store: any) => {
      const recs = getStoreRecommendations(store.id);
      return {
        ...store,
        recommendations: recs,
        state: store.geo?.state || '',
        city: store.geo?.city || '',
        zipcode: store.zipcode || store.geo?.zipcode || '',
      };
    });
    exportOpportunitiesByRegionData(exportStores, listName);
    toast.success(`Downloaded ${selected.length} stores`);
  };

  const handleRemoveFromList = async () => {
    if (selectedStoreIds.size === 0) return;
    const poiIds = Array.from(selectedStoreIds);
    const result = await removeStoresFromList(listName, poiIds);
    if (result.success) {
      toast.success(`Removed ${result.removedCount} store${result.removedCount !== 1 ? 's' : ''} from list`);
      setStores(prev => prev.filter((s: any) => !selectedStoreIds.has(s.id)));
      setSelectedStoreIds(new Set());
    } else {
      toast.error(result.error || 'Failed to remove stores');
    }
  };

  const toggleStoreSelection = (storeId: string) => {
    setSelectedStoreIds(prev => {
      const next = new Set(prev);
      if (next.has(storeId)) next.delete(storeId);
      else next.add(storeId);
      return next;
    });
  };

  const handleViewDetails = (store: any) => {
    const recs = getStoreRecommendations(store.id);
    const solutions = getSolutions(store);
    const whitespaceArr: string[] = [];
    if (!store.purchek_install_date) whitespaceArr.push('Purchek');
    if (!store.containment_install_date) whitespaceArr.push('Cart Containment');
    if (!store.cart_manager_install_date) whitespaceArr.push('Cart Manager');
    if (!store.subscription_start_date) whitespaceArr.push('Subscription');
    if (!store.paired_wheel_install_date) whitespaceArr.push('Paired Wheel');

    const detail: StoreDetailInfo = {
      poiId: store.id,
      site: store.brand_name || store.site || '',
      storeId: store.id,
      address: store.address || '',
      city: store.geo?.city ? capitalizeWords(store.geo.city) : '-',
      state: store.geo?.state ? capitalizeWords(store.geo.state) : '-',
      zipCode: store.zipcode || store.geo?.zipcode || '',
      status: store.customer_id ? 'Customer' : 'Non-Customer',
      ultimateParent: store.brand_name || '',
      channel: store.channel?.toLowerCase() === 'diy' ? 'DIY' : (store.channel || 'Unknown'),
      crimeRate: store.geo?.crime_rate ?? '-',
      gkap: store.nearby_penetration?.penetration != null ? `${store.nearby_penetration.penetration.toFixed(2)}%` : '-',
      solutions: solutions.length > 0 ? solutions.join(', ') : 'None',
      whitespace: whitespaceArr,
      lifetimeValue: store.lifetime_value ?? undefined,
      hasPurchek: recs.purchek.length > 0,
      hasContainment: recs.containment.length > 0,
      hasCartManager: recs.cartManager.length > 0,
      hasSubscription: recs.subscription.length > 0,
      hasPairedWheel: recs.pairedWheel.length > 0,
      crimeIncidents: store.geo?.total_crime_incidents ?? 0,
      shopliftingAmountUsd: store.nearby_penetration?.shoplifting_amount_usd ?? null,
      shopliftingIncidentCount: store.nearby_penetration?.shoplifting_incident_count ?? null,
      totalCrimeAmountUsd: store.video?.total_crime_amount_usd ?? null,
      videoIncidentCount: store.video?.incident_count ?? null,
      firstPurchaseDate: store.first_purchase ?? null,
      lastPurchaseDate: store.last_purchase ?? null,
      // Additional fields (dummy values for now)
      sitesAcquired: 12,
      upsellOpportunity: 'High',
      winBack: 'Medium',
      subscriptionSites: 8,
      cartOrdinance: store.geo?.penalty_for_cart_owner ?? null,
      storeDensity: 'Medium',
      // Neighbourhood & Environment
      highwayProximity: store.nearby_penetration?.highway_proximity ?? null,
      storesWithin5mi: store.nearby_penetration?.total_density ?? null,
      heatDays: store.geo?.no_of_hot_days ?? null,
      coldDays: store.geo?.no_of_cold_days ?? null,
      busStopProximity: null,
      lat: store.latitude ?? null,
      lng: store.longitude ?? null,
    };
    setSelectedStoreDetail(detail);
    setStoreDetailModalOpen(true);
  };

  const handleNotesClick = (storeName: string, poiId: string) => {
    setSelectedStoreForNotes(storeName);
    setSelectedStorePoiId(poiId);
    setNotesDialogOpen(true);
  };

  const handleNoteAdded = () => {
    if (!selectedStorePoiId) return;
    const poiId = selectedStorePoiId;
    const load = async () => {
      const notes = await fetchStoreNotes(poiId);
      setStores(prev => prev.map(store =>
        store.id === poiId ? { ...store, store_notes: notes } : store
      ));
    };
    load();
  };

  useEffect(() => {
    if (!listId || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(listId)) {
      toast.error('Invalid list');
      navigate('/dashboard/your-lists');
      return;
    }
    const load = async () => {
      setLoading(true);
      const listInfo = await fetchListById(listId);
      if (!listInfo) {
        toast.error('List not found');
        navigate('/dashboard/your-lists');
        return;
      }
      setListName(listInfo.list_name);
      // Determine edit permission: owner always can edit, shared users need 'edit' permission, org access needs 'edit'
      const currentUserId = (useAuthStore.getState().user as any)?.id;
      const isOwner = listInfo.user_id === currentUserId;
      if (isOwner) {
        setCanEdit(true);
      } else if (listInfo.shared_permission === 'edit') {
        setCanEdit(true);
      } else if (!listInfo.shared_permission && listInfo.org_access === 'edit') {
        setCanEdit(true);
      } else {
        setCanEdit(false);
      }
      if (listInfo.org_access) setShareOrgAccess(listInfo.org_access as 'view' | 'edit');
      const rawStores = await fetchListStoresFull(listInfo.list_name);
      setStores(Array.isArray(rawStores) ? rawStores : []);
      // Load comment counts per store
      fetchListComments(listInfo.list_name).then((allComments: any) => {
        let total = 0;
        const counts = new Map<string, number>();
        allComments.forEach((c: any) => {
          total += 1 + (c.replies?.length || 0);
          if (c.poi_id) {
            const prev = counts.get(c.poi_id) || 0;
            counts.set(c.poi_id, prev + 1 + (c.replies?.length || 0));
          }
        });
        setStoreCommentCounts(counts);
        setCommentCount(total);
      });
      setLoading(false);
    };
    load();
  }, [listId, navigate]);

  const getSolutions = (store: any): string[] => {
    const arr: string[] = [];
    if (store.purchek_install_date) arr.push('Purchek');
    if (store.containment_install_date) arr.push('Cart Containment');
    if (store.cart_manager_install_date) arr.push('Cart Manager');
    if (store.subscription_start_date) arr.push('Subscription');
    if (store.paired_wheel_install_date) arr.push('Paired Wheel');
    return arr;
  };

  const getStatus = (store: any) => store.customer_id ? 'Customer' : 'Non-Customer';

  const getCrimeIndex = (crimeIncidents: number | undefined): string => {
    if (!crimeIncidents) return 'No Data';
    if (crimeIncidents >= 5000) return 'Critical';
    if (crimeIncidents >= 1000) return 'High';
    if (crimeIncidents >= 200) return 'Medium';
    if (crimeIncidents > 0) return 'Low';
    return 'No Data';
  };

  const getCrimeBadgeClass = (label: string): string => {
    switch (label) {
      case 'Critical': case 'High': return 'so-crime-badge high';
      case 'Medium': return 'so-crime-badge medium';
      case 'Low': return 'so-crime-badge low';
      default: return 'so-crime-badge';
    }
  };

  const getStoreDensityLabel = (density: number | null | undefined): string => {
    if (density == null || density === 0) return 'None';
    if (density >= 10) return 'High';
    if (density >= 5) return 'Medium';
    return 'Low';
  };

  const getHighwayProximityLabel = (proximity: string | null | undefined): string => {
    if (!proximity) return 'No Data';
    const miles = parseFloat(proximity);
    if (isNaN(miles)) return proximity;
    if (miles <= 1) return 'Very Close';
    if (miles <= 5) return 'Near';
    if (miles <= 15) return 'Moderate';
    return 'Far';
  };

  const getWhitespace = (store: any): string[] => {
    const arr: string[] = [];
    if (!store.purchek_install_date) arr.push('Purchek');
    if (!store.containment_install_date) arr.push('Cart Containment');
    if (!store.cart_manager_install_date) arr.push('Cart Manager');
    if (!store.subscription_start_date) arr.push('Subscription');
    if (!store.paired_wheel_install_date) arr.push('Paired Wheel');
    return arr;
  };

  const storeRecommendations = useMemo(() => {
    const map = new Map<string, ReturnType<typeof calculateStoreRecommendations>>();
    stores.forEach((s: any) => {
      const recs = calculateStoreRecommendations(
        s,
        (s as any).estimated_no_of_carts_per_store || 0,
      );
      map.set(s.id, recs);
    });
    return map;
  }, [stores]);

  const getStoreRecommendations = (storeId: string) => {
    return storeRecommendations.get(storeId) || {
      purchek: [], containment: [], cartManager: [], subscription: [], pairedWheel: [],
      purchekBadge: { matches: false, tooltip: '' },
      cartManagerBadge: { matches: false, tooltip: '' },
      containmentBadge: { matches: false, tooltip: '' },
      subscriptionBadge: { matches: false, tooltip: '' },
      pairedWheelBadge: { matches: false, tooltip: '' },
    };
  };

  const totalOpportunities = useMemo(() => {
    let count = 0;
    stores.forEach((s: any) => {
      const recs = storeRecommendations.get(s.id);
      if (!recs) return;
      if (recs.purchek.length > 0) count++;
      if (recs.containment.length > 0) count++;
      if (recs.cartManager.length > 0) count++;
      if (recs.subscription.length > 0) count++;
      if (recs.pairedWheel.length > 0) count++;
    });
    return count;
  }, [stores, storeRecommendations]);

  const criticalRiskSites = useMemo(() => {
    return stores.filter((s: any) => {
      const incidents = s.geo?.total_crime_incidents ?? 0;
      return incidents >= 1000; // High + Critical
    }).length;
  }, [stores]);

  // Shortcut filter counts
  const anyOpportunityCount = useMemo(() => stores.filter((s: any) => {
    const recs = storeRecommendations.get(s.id);
    return recs && (recs.purchek.length > 0 || recs.containment.length > 0 || recs.cartManager.length > 0 || recs.subscription.length > 0 || recs.pairedWheel.length > 0);
  }).length, [stores, storeRecommendations]);
  const purchekCount = useMemo(() => stores.filter((s: any) => { const r = storeRecommendations.get(s.id); return r && r.purchek.length > 0; }).length, [stores, storeRecommendations]);
  const containmentCount = useMemo(() => stores.filter((s: any) => { const r = storeRecommendations.get(s.id); return r && r.containment.length > 0; }).length, [stores, storeRecommendations]);
  const cartManagerCount = useMemo(() => stores.filter((s: any) => { const r = storeRecommendations.get(s.id); return r && r.cartManager.length > 0; }).length, [stores, storeRecommendations]);
  const subscriptionCount = useMemo(() => stores.filter((s: any) => { const r = storeRecommendations.get(s.id); return r && r.subscription.length > 0; }).length, [stores, storeRecommendations]);
  const pairedWheelCount = useMemo(() => stores.filter((s: any) => { const r = storeRecommendations.get(s.id); return r && r.pairedWheel.length > 0; }).length, [stores, storeRecommendations]);

  const matchesQuickFilter = useCallback((store: any): boolean => {
    if (activeFilters.size === 0) return true;
    const recs = storeRecommendations.get(store.id);
    return Array.from(activeFilters).every(filter => {
      switch (filter) {
        case 'any-opportunity':
          return !!(recs && (recs.purchek.length > 0 || recs.containment.length > 0 || recs.cartManager.length > 0 || recs.subscription.length > 0 || recs.pairedWheel.length > 0));
        case 'purchek':
          return (recs && recs.purchek.length > 0) ?? false;
        case 'containment':
          return (recs && recs.containment.length > 0) ?? false;
        case 'cart-manager':
          return (recs && recs.cartManager.length > 0) ?? false;
        case 'subscription':
          return (recs && recs.subscription.length > 0) ?? false;
        case 'paired-wheel':
          return (recs && recs.pairedWheel.length > 0) ?? false;
        case 'high-crime':
          return (store.geo?.total_crime_incidents ?? 0) >= 1000 && (store.geo?.total_crime_incidents ?? 0) < 5000;
        case 'critical-crime':
          return (store.geo?.total_crime_incidents ?? 0) >= 5000;
        case 'cart-ordinance':
          return !!store.geo?.penalty_for_cart_owner;
        case 'extreme-weather':
          return (store.geo?.no_of_hot_days ?? 0) >= 50 || (store.geo?.no_of_cold_days ?? 0) >= 50;
        default:
          return true;
      }
    });
  }, [activeFilters, storeRecommendations]);

  const filteredStores = useMemo(() => {
    return stores.filter((s: any) => {
      if (!matchesQuickFilter(s)) return false;
      const name = (s.site || s.brand_name || '').toLowerCase();
      const addr = (s.address || '').toLowerCase();
      const city = (s.geo?.city || '').toLowerCase();
      const zip = (s.zipcode || s.geo?.zipcode || '').toLowerCase();
      const q = searchQuery.toLowerCase();
      return !q || name.includes(q) || addr.includes(q) || city.includes(q) || zip.includes(q);
    });
  }, [stores, searchQuery, matchesQuickFilter]);

  const isAllFilteredSelected = filteredStores.length > 0 && filteredStores.every((s: any) => selectedStoreIds.has(s.id));

  const toggleSelectAll = () => {
    if (isAllFilteredSelected) {
      setSelectedStoreIds(new Set());
    } else {
      setSelectedStoreIds(new Set(filteredStores.map((s: any) => s.id)));
    }
  };

  const mapStores: MapStore[] = useMemo(() => {
    return filteredStores
      .filter((s: any) => s.latitude && s.longitude)
      .map((s: any) => ({
        id: s.id || String(s.id),
        brand_name: s.brand_name ?? undefined,
        site: s.site ?? undefined,
        latitude: s.latitude,
        longitude: s.longitude,
        customer_id: s.customer_id ?? undefined,
        channel: s.channel ?? undefined,
        address: s.address ?? undefined,
        city: s.geo?.city ?? undefined,
        state: s.geo?.state ?? undefined,
        zipcode: s.zipcode || s.geo?.zipcode || undefined,
        purchek_install_date: s.purchek_install_date ?? undefined,
        containment_install_date: s.containment_install_date ?? undefined,
        cart_manager_install_date: s.cart_manager_install_date ?? undefined,
        subscription_start_date: s.subscription_start_date ?? undefined,
        paired_wheel_install_date: s.paired_wheel_install_date ?? undefined,
        estimated_no_of_carts_per_store: s.estimated_no_of_carts_per_store ?? undefined,
        geo: {
          city: s.geo?.city ?? undefined,
          state_code: s.geo?.state ?? undefined,
          total_crime_incidents: s.geo?.total_crime_incidents ?? 0,
        },
        nearby_penetration: {
          plaza_cm_customer_count: s.plaza_cm_customer_count ?? 0,
        },
        recommendations: s.recommendations,
        total_crime_amount_usd: s.total_crime_amount_usd ?? null,
        video_incident_count: s.video_incident_count ?? null,
        is_customer: !!s.customer_id,
      }));
  }, [filteredStores]);

  const handleStreetViewClick = useCallback((position: { lat: number; lng: number }, name?: string) => {
    setStreetViewStore({ lat: position.lat, lng: position.lng, name });
  }, []);

  if (loading) {
    return (
      <div className="ls-container">
        <div className="ls-loading"><Spinner size="small" /></div>
      </div>
    );
  }

  return (
    <div className="ls-container">
      {/* Breadcrumb */}
      <div className="ls-breadcrumb">
        <span className="ls-breadcrumb-link" onClick={() => navigate('/dashboard/your-lists')}>My Lists</span>
        <HiChevronRight size={12} className="ls-breadcrumb-sep" />
        <span className="ls-breadcrumb-current">{listName}</span>
      </div>

      {/* Header */}
      <div className="ls-header">
        <div className="ls-header-left">
          <h1 className="ls-title">{listName}</h1>
          <div className="ls-meta">
            <span className="ls-meta-item">{stores.length} sites</span>
            <span className="ls-meta-dot" />
            <span className="ls-meta-item ls-meta-blue">{totalOpportunities} opportunities</span>
            <span className="ls-meta-dot" />
            <span className="ls-meta-item ls-meta-red">{criticalRiskSites} critical-risk sites</span>
            <span className="ls-meta-dot" />
            <span className="ls-meta-item ls-meta-muted">Updated {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
          </div>
        </div>
        <div className="ls-header-right">
          <button className="ls-action-btn" onClick={() => { setOpenCommentStoreId(null); setShowComments(true); }}>
            <HiOutlineChatBubbleLeftRight size={15} />
            Comments
            {commentCount > 0 && <span className="ls-comment-badge">{commentCount}</span>}
          </button>
          {canEdit && (
            <button className="ls-action-btn" onClick={handleOpenShare}>
              <HiOutlineShare size={15} />
              Share
            </button>
          )}
        </div>
      </div>

      <div className="so-content">
        <div className="ls-table-box">
          {/* Controls */}
          <div className="ls-controls">
            <div className="ls-controls-left">
              <div className="ls-view-toggle">
                <button className={`ls-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Table View">
                  <HiOutlineListBullet />
                </button>
                <button className={`ls-toggle-btn ${viewMode === 'map' ? 'active' : ''}`} onClick={() => setViewMode('map')} title="Map View">
                  <HiOutlineMapPin />
                </button>
              </div>
              <div className="ls-search-box">
                <HiOutlineMagnifyingGlass className="ls-search-icon" />
                <input
                  type="text"
                  placeholder="Search stores..."
                  className="ls-search-input"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button className="ls-search-clear" onClick={() => setSearchQuery('')}><HiX size={14} /></button>
                )}
              </div>
            </div>
            <div className="ls-controls-right">
              <span className="ls-store-count">Showing <strong>{filteredStores.length}</strong> of <strong>{stores.length}</strong> sites</span>
            </div>
          </div>

          <div className="shortcuts-filters">
            <button
              className={`filter-btn blue ${activeFilters.has('any-opportunity') ? 'active' : ''}`}
              onClick={() => {
                if (activeFilters.has('any-opportunity')) {
                  setActiveFilters(new Set<FilterType>());
                } else {
                  setActiveFilters(new Set<FilterType>(['any-opportunity']));
                }
              }}
            >
              <img src={require('../../assets/solution-icons/AnyOpportuinity.png')} alt="Any Opportunity" className="filter-icon" />
              Sites with Any Opportunity
              <span className="filter-count blue">{anyOpportunityCount}</span>
            </button>
            <button
              className={`filter-btn blue ${activeFilters.has('purchek') ? 'active' : ''}`}
              onClick={() => {
                const newFilters = new Set(activeFilters);
                newFilters.delete('any-opportunity');
                if (newFilters.has('purchek')) newFilters.delete('purchek');
                else newFilters.add('purchek');
                setActiveFilters(newFilters);
              }}
            >
              <img src={require('../../assets/solution-icons/Purchek.png')} alt="Purchek" className="filter-icon" />
              Purchek
              <span className="filter-count blue">{purchekCount}</span>
            </button>
            <button
              className={`filter-btn cyan ${activeFilters.has('containment') ? 'active' : ''}`}
              onClick={() => {
                const newFilters = new Set(activeFilters);
                newFilters.delete('any-opportunity');
                if (newFilters.has('containment')) newFilters.delete('containment');
                else newFilters.add('containment');
                setActiveFilters(newFilters);
              }}
            >
              <img src={require('../../assets/solution-icons/CartControl.png')} alt="Cart Containment" className="filter-icon" />
              Cart Containment
              <span className="filter-count cyan">{containmentCount}</span>
            </button>
            <button
              className={`filter-btn green ${activeFilters.has('cart-manager') ? 'active' : ''}`}
              onClick={() => {
                const newFilters = new Set(activeFilters);
                newFilters.delete('any-opportunity');
                if (newFilters.has('cart-manager')) newFilters.delete('cart-manager');
                else newFilters.add('cart-manager');
                setActiveFilters(newFilters);
              }}
            >
              <img src={require('../../assets/solution-icons/CartManager.png')} alt="Cart Manager" className="filter-icon" />
              Cart Manager
              <span className="filter-count green">{cartManagerCount}</span>
            </button>
            <button
              className={`filter-btn purple ${activeFilters.has('subscription') ? 'active' : ''}`}
              onClick={() => {
                const newFilters = new Set(activeFilters);
                newFilters.delete('any-opportunity');
                if (newFilters.has('subscription')) newFilters.delete('subscription');
                else newFilters.add('subscription');
                setActiveFilters(newFilters);
              }}
            >
              <img src={require('../../assets/solution-icons/Subscription.png')} alt="Subscription" className="filter-icon" />
              Subscription
              <span className="filter-count purple">{subscriptionCount}</span>
            </button>
            <button
              className={`filter-btn orange ${activeFilters.has('paired-wheel') ? 'active' : ''}`}
              onClick={() => {
                const newFilters = new Set(activeFilters);
                newFilters.delete('any-opportunity');
                if (newFilters.has('paired-wheel')) newFilters.delete('paired-wheel');
                else newFilters.add('paired-wheel');
                setActiveFilters(newFilters);
              }}
            >
              <img src={require('../../assets/solution-icons/PairedWheel.png')} alt="Paired Wheel" className="filter-icon" />
              Paired Wheel
              <span className="filter-count orange">{pairedWheelCount}</span>
            </button>
          </div>

          {viewMode === 'map' ? (
            <div className="ls-map-container">
              {filteredStores.length === 0 ? (
                <div className="ls-empty"><HiOutlineOfficeBuilding size={40} /><p>No stores to display</p></div>
              ) : (
                <>
                  <div className="allsites-map-inline" style={{ position: 'relative' }}>
                    <GoogleMapView
                      stores={mapStores}
                      fitBounds={true}
                      showHeatmap={showHeatmap}
                      showDetailedMarkers={true}
                      onStreetViewClick={handleStreetViewClick}
                      height="100%"
                    />
                    <HeatmapToggle showHeatmap={showHeatmap} onToggle={setShowHeatmap} />
                    <div className="map-legend">
                      <span className="map-legend-item"><span className="map-legend-dot blue"></span>Customer</span>
                      <span className="map-legend-item"><span className="map-legend-dot red"></span>Non-Customer</span>
                      <span className="map-legend-divider"></span>
                      <span className="map-legend-item"><span className="map-legend-badge">P</span>Purchek</span>
                      <span className="map-legend-item"><span className="map-legend-badge">CC</span>Cart Containment</span>
                      <span className="map-legend-item"><span className="map-legend-badge">CM</span>Cart Manager</span>
                      <span className="map-legend-item"><span className="map-legend-badge">S</span>Subscription</span>
                    </div>
                  </div>
                  {streetViewStore && (
                    <StreetViewPanel
                      lat={streetViewStore.lat}
                      lng={streetViewStore.lng}
                      storeName={streetViewStore.name}
                      onClose={() => setStreetViewStore(null)}
                    />
                  )}
                </>
              )}
            </div>
          ) : (
            <>
          {filteredStores.length === 0 ? (
            <div className="ls-empty"><HiOutlineOfficeBuilding size={40} /><p>{stores.length === 0 ? 'No stores in this list' : 'No stores match your search'}</p></div>
          ) : (
            <>
            {selectedStoreIds.size > 0 && (
              <div className="selection-action-strip">
                <div className="selection-action-strip-left">
                  <span className="selection-action-strip-label">{selectedStoreIds.size} selected</span>
                  <button className="selection-action-strip-btn download" onClick={handleDownloadSelected}>
                    <svg className="selection-action-strip-icon" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Download as .xlsx
                  </button>
                  {canEdit && (
                    <button className="selection-action-strip-btn remove" onClick={handleRemoveFromList}>
                      <HiOutlineTrash size={14} />
                      Remove from List
                    </button>
                  )}
                </div>
                <button
                  className="selection-action-strip-btn clear"
                  onClick={() => setSelectedStoreIds(new Set())}
                >
                  Clear
                </button>
              </div>
            )}
            <div className="ls-table-scroll">
              <table className="so-stores-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        className="store-checkbox"
                        checked={isAllFilteredSelected}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th style={{ width: '40px' }}>Rank</th>
                    <th style={{ width: '40px' }}></th>
                    <th>Store</th>
                    <th>Status</th>
                    <th>
                      <Tooltip content={getTooltipText('installedSolutions')} side="top">
                        <div>Installed Solutions</div>
                      </Tooltip>
                    </th>
                    <th>Customer Since</th>
                    <th style={{ minWidth: '200px' }}>
                      <Tooltip content={getTooltipText('whitespace')} side="top">
                        <div>Whitespace</div>
                      </Tooltip>
                    </th>
                    <th style={{ minWidth: '220px' }}>
                      <Tooltip content={getTooltipText('recommendedSolutions')} side="top">
                        <div>Recommended Solutions</div>
                      </Tooltip>
                    </th>
                    <th>Crime Index</th>
                    <th>
                      <Tooltip content="Gatekeeper Account Penetration – the percentage of customer stores / total stores within 5 mile radius of the reference store" side="top">
                        <div>GK Account Penetration</div>
                      </Tooltip>
                    </th>
                    <th>Highway Proximity</th>
                    <th>Cart Ordinance</th>
                    <th>Weather</th>
                    <th>Total Recovery</th>
                    <th>Pushout Prevented</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStores.map((store: any, idx: number) => {
                    const solutions = getSolutions(store);
                    const whitespace = getWhitespace(store);
                    const crimeLabel = getCrimeIndex(store.geo?.total_crime_incidents);
                    const hotDays = store.geo?.no_of_hot_days ?? 0;
                    const coldDays = store.geo?.no_of_cold_days ?? 0;
                    const recs = getStoreRecommendations(store.id);
                    const hasRecs = recs.purchek.length > 0 || recs.containment.length > 0 || recs.cartManager.length > 0 || recs.subscription.length > 0 || recs.pairedWheel.length > 0;
                    const recList: { type: string; label: string }[] = [];
                    if (recs.purchek.length > 0) recList.push({ type: 'purchek', label: 'Purchek' });
                    if (recs.containment.length > 0) recList.push({ type: 'containment', label: 'Containment' });
                    if (recs.cartManager.length > 0) recList.push({ type: 'cartManager', label: 'Cart Manager' });
                    if (recs.subscription.length > 0) recList.push({ type: 'subscription', label: 'Subscription' });
                    if (recs.pairedWheel.length > 0) recList.push({ type: 'pairedWheel', label: 'Paired Wheel' });
                    return (
                    <React.Fragment key={store.id || idx}>
                    <tr className={expandedRow === store.id ? 'so-row-expanded' : ''} onClick={() => setExpandedRow(expandedRow === store.id ? null : store.id)} style={{ cursor: 'pointer' }}>
                      <td>
                        <input
                          type="checkbox"
                          className="store-checkbox"
                          checked={selectedStoreIds.has(store.id)}
                          onChange={() => toggleStoreSelection(store.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className="rank-badge">{idx + 1}</span>
                      </td>
                      <td>
                        <button className="so-expand-btn" onClick={(e) => { e.stopPropagation(); setExpandedRow(expandedRow === store.id ? null : store.id); }}>
                          <HiChevronRight className={`so-expand-icon ${expandedRow === store.id ? 'expanded' : ''}`} />
                        </button>
                      </td>
                      <td>
                        <div className="so-store-cell">
                          <div className="so-store-name" style={{ position: 'relative' }}>
                            <span>{store.brand_name || store.site || '-'}{store.store_number ? ` ${store.store_number}` : ''}</span>
                            {store.store_notes && store.store_notes.length > 0 && (
                              <HiOutlinePencilAlt className="store-indicator-icon" title={`${store.store_notes.length} note${store.store_notes.length > 1 ? 's' : ''}`} />
                            )}
                            {(storeCommentCounts.get(store.id) || 0) > 0 && (
                              <HiOutlineChatBubbleLeftRight className="store-indicator-icon" title={`${storeCommentCounts.get(store.id)} comment${(storeCommentCounts.get(store.id) || 0) > 1 ? 's' : ''}`} />
                            )}
                            {(store.nearby_penetration?.plaza_cm_customer_count ?? 0) > 0 && (
                              <HiOutlineOfficeBuilding className="plaza-icon" title="Part of Shopping Complex" />
                            )}
                          </div>
                          <div className="so-store-id">{store.id || ''}</div>
                          <div className="so-store-address">{store.address || '-'}</div>
                          <div className="so-store-location">
                            {store.geo?.city ? capitalizeWords(store.geo.city) : ''}{store.geo?.city && store.geo?.state ? ', ' : ''}{store.geo?.state ? capitalizeWords(store.geo.state) : ''}
                            {store.zipcode ? ` ${store.zipcode}` : ''}
                          </div>
                          <Tooltip
                            content={!store.nearby_penetration?.total_density ? 'No stores around 0.5 miles radius' : `${store.nearby_penetration.total_density} stores in 0.5 miles radius`}
                            side="top"
                          >
                            <span className={`store-density-tag density-${getStoreDensityLabel(store.nearby_penetration?.total_density).toLowerCase()}`} style={{ cursor: 'default' }}>
                              Store Density : {getStoreDensityLabel(store.nearby_penetration?.total_density)}
                            </span>
                          </Tooltip>
                          <div className="store-hover-actions">
                            <button
                              className="map-btn"
                              onClick={(e) => { e.stopPropagation(); handleViewDetails(store); }}
                              title="View Store Details"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                              <span>Details</span>
                            </button>
                            <button
                              className="add-notes-btn-sites"
                              title="Add Notes"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNotesClick(store.brand_name || store.site || '', store.id);
                              }}
                            >
                              <HiOutlinePencilAlt className="notes-icon-sites" />
                              <span>Notes</span>
                              {store.store_notes && store.store_notes.length > 0 && (
                                <span className="notes-badge">{store.store_notes.length}</span>
                              )}
                            </button>
                            <button
                              className="add-notes-btn-sites"
                              onClick={(e) => { e.stopPropagation(); setOpenCommentStoreId(store.id); setShowComments(true); }}
                              title="View store comments"
                            >
                              <HiOutlineChatBubbleLeftRight className="notes-icon-sites" />
                              <span>Comment</span>
                              {(storeCommentCounts.get(store.id) || 0) > 0 && (
                                <span className="notes-badge">{storeCommentCounts.get(store.id)}</span>
                              )}
                            </button>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`so-status-badge ${getStatus(store) === 'Customer' ? 'customer' : 'non-customer'}`}>
                          {getStatus(store)}
                        </span>
                      </td>
                      <td className="solutions-cell">
                        <div className="solutions-container">
                          {solutions.length > 0 ? (
                            <>
                              {solutions.slice(0, expandedSolutions.has(store.id) ? undefined : 2).map((s, i) => (
                                <span key={i} className="solution-badge installed">{s}</span>
                              ))}
                              {!expandedSolutions.has(store.id) && solutions.length > 2 && (
                                <button
                                  className="solution-badge installed expand-more"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const n = new Set(expandedSolutions);
                                    n.add(store.id);
                                    setExpandedSolutions(n);
                                  }}
                                >
                                  +{solutions.length - 2}
                                </button>
                              )}
                              {expandedSolutions.has(store.id) && solutions.length > 2 && (
                                <button
                                  className="solution-badge installed expand-less"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const n = new Set(expandedSolutions);
                                    n.delete(store.id);
                                    setExpandedSolutions(n);
                                  }}
                                >
                                  - Less
                                </button>
                              )}
                            </>
                          ) : (
                            <span className="solution-text">{getStatus(store) === 'Non-Customer' ? 'No Solution Installed' : 'None'}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="last-purchase-text">{store.first_purchase || '-'}</span>
                      </td>
                      <td className="whitespace-cell">
                        <div className="solutions-container">
                          {whitespace.length > 0 ? (
                            <>
                              {whitespace.slice(0, expandedWhitespace.has(store.id) ? undefined : 2).map((ws, i) => (
                                <span key={i} className="solution-badge whitespace">{ws}</span>
                              ))}
                              {!expandedWhitespace.has(store.id) && whitespace.length > 2 && (
                                <button
                                  className="solution-badge whitespace expand-more"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const n = new Set(expandedWhitespace);
                                    n.add(store.id);
                                    setExpandedWhitespace(n);
                                  }}
                                >
                                  +{whitespace.length - 2}
                                </button>
                              )}
                              {expandedWhitespace.has(store.id) && whitespace.length > 2 && (
                                <button
                                  className="solution-badge whitespace expand-less"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const n = new Set(expandedWhitespace);
                                    n.delete(store.id);
                                    setExpandedWhitespace(n);
                                  }}
                                >
                                  - Less
                                </button>
                              )}
                            </>
                          ) : (
                            <Tooltip content="The store is already protected" side="top">
                              <span className="solution-text">None</span>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                      <td className="opportunity-cell">
                        <div className="opportunity-container">
                          {hasRecs ? (
                            <>
                              {(() => {
                                const displayCount = expandedSolutions.has(store.id) ? recList.length : Math.min(2, recList.length);
                                return (
                                  <>
                                    {recList.slice(0, displayCount).map((rec, i) => (
                                      <span key={i} className={`recommendation-badge ${rec.type}`}>{rec.label}</span>
                                    ))}
                                    {!expandedSolutions.has(store.id) && recList.length > 2 && (
                                      <button
                                        className="solution-badge installed expand-more"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const n = new Set(expandedSolutions);
                                          n.add(store.id);
                                          setExpandedSolutions(n);
                                        }}
                                      >
                                        +{recList.length - 2}
                                      </button>
                                    )}
                                    {expandedSolutions.has(store.id) && recList.length > 2 && (
                                      <button
                                        className="solution-badge installed expand-less"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const n = new Set(expandedSolutions);
                                          n.delete(store.id);
                                          setExpandedSolutions(n);
                                        }}
                                      >
                                        - Less
                                      </button>
                                    )}
                                  </>
                                );
                              })()}
                            </>
                          ) : (
                            <span className="solution-text" title="No recommended solution based on the given Recommendation Configuration. The store may still be at risk and need protection">None</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <Tooltip
                          content={store.geo?.total_crime_incidents ? `${store.geo.total_crime_incidents.toLocaleString()} Annual Relevant Crimes Reported` : 'Data not available'}
                          side="top"
                        >
                          <span className={getCrimeBadgeClass(crimeLabel)} style={{ cursor: 'default' }}>{crimeLabel}</span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip
                          content={store.nearby_penetration?.penetration ? `Nearby penetration within 5 miles radius: ${store.nearby_penetration.penetration.toFixed(2)}%` : 'No nearby penetration data available'}
                          side="top"
                        >
                          <span style={{ cursor: 'default' }}>{store.nearby_penetration?.penetration != null ? `${store.nearby_penetration.penetration.toFixed(2)}%` : '-'}</span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip
                          content={store.nearby_penetration?.highway_proximity ? `${store.nearby_penetration.highway_proximity} miles to nearest highway` : 'Data not available'}
                          side="top"
                        >
                          <span className={`highway-proximity-badge ${getHighwayProximityLabel(store.nearby_penetration?.highway_proximity).toLowerCase().replace(' ', '-')}`} style={{ cursor: 'default' }}>
                            {store.nearby_penetration?.highway_proximity ? `${parseFloat(store.nearby_penetration.highway_proximity).toFixed(1)} mi` : 'No Data'}
                          </span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip
                          content={
                            store.geo?.penalty_for_cart_owner
                              ? (store.geo?.law_summary || 'Cart abandonment ordinance is active in this state.')
                              : 'No cart abandonment ordinance in this state.'
                          }
                          side="top"
                        >
                          <span className={`so-penalty-badge ${store.geo?.penalty_for_cart_owner ? 'yes' : 'no'}`} style={{ cursor: 'default' }}>
                            {store.geo?.penalty_for_cart_owner ? 'Yes' : 'No'}
                          </span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip
                          content={
                            (hotDays || coldDays)
                              ? `Hot Days (Extreme Heat 90°F or higher): ${hotDays} | Cold Days (Extreme Cold 32°F or lower): ${coldDays}`
                              : 'Data not available'
                          }
                          side="top"
                        >
                          <div className="so-weather-cell" style={{ cursor: 'default' }}>
                            {hotDays > 0 && (
                              <span className="so-weather-item hot">
                                <HiOutlineSun size={14} />
                                {hotDays}
                              </span>
                            )}
                            {coldDays > 0 && (
                              <span className="so-weather-item cold">
                                <BsSnow size={12} />
                                {coldDays}
                              </span>
                            )}
                            {!hotDays && !coldDays && <span className="so-weather-item none">-</span>}
                          </div>
                        </Tooltip>
                      </td>
                      <td className="recovery-cell">
                        {(store.nearby_penetration?.shoplifting_amount_usd != null || store.video?.total_crime_amount_usd != null)
                          ? <Tooltip content={`Neighbourhood Recovery: ${store.nearby_penetration?.shoplifting_amount_usd != null ? `$${Math.round(store.nearby_penetration.shoplifting_amount_usd).toLocaleString()}` : 'N/A'} | Store Recovery: ${store.video?.total_crime_amount_usd != null ? `$${Math.round(store.video.total_crime_amount_usd).toLocaleString()}` : 'N/A'}`} side="top">
                              <div className="recovery-rows">
                                {store.nearby_penetration?.shoplifting_amount_usd != null && (
                                  <div className="recovery-row">
                                    <span className="recovery-label">Neighbourhood</span>
                                    <span className="recovery-value">${Math.round(store.nearby_penetration.shoplifting_amount_usd).toLocaleString()}</span>
                                  </div>
                                )}
                                {store.video?.total_crime_amount_usd != null && (
                                  <div className="recovery-row">
                                    <span className="recovery-label">Site</span>
                                    <span className="recovery-value">${Math.round(store.video.total_crime_amount_usd).toLocaleString()}</span>
                                  </div>
                                )}
                              </div>
                            </Tooltip>
                          : <span className="so-solution-none">No Data</span>}
                      </td>
                      <td className="recovery-cell">
                        {(store.nearby_penetration?.shoplifting_incident_count != null || store.video?.incident_count != null)
                          ? <Tooltip content={`Neighbourhood Pushouts Prevented: ${store.nearby_penetration?.shoplifting_incident_count != null ? store.nearby_penetration.shoplifting_incident_count.toLocaleString() : 'N/A'} | Store Pushouts Prevented: ${store.video?.incident_count != null ? store.video.incident_count.toLocaleString() : 'N/A'}`} side="top">
                              <div className="recovery-rows">
                                {store.nearby_penetration?.shoplifting_incident_count != null && (
                                  <div className="recovery-row">
                                    <span className="recovery-label">Neighbourhood</span>
                                    <span className="recovery-value">{store.nearby_penetration.shoplifting_incident_count.toLocaleString()}</span>
                                  </div>
                                )}
                                {store.video?.incident_count != null && (
                                  <div className="recovery-row">
                                    <span className="recovery-label">Site</span>
                                    <span className="recovery-value">{store.video.incident_count.toLocaleString()}</span>
                                  </div>
                                )}
                              </div>
                            </Tooltip>
                          : <span className="so-solution-none">No Data</span>}
                      </td>
                    </tr>
                    {expandedRow === store.id && (
                      <tr className="so-expanded-content">
                        <td colSpan={20}>
                          <div className="so-expanded-details">
                            {hasRecs ? (
                              <div className="so-recommendations-section">
                                <div className="so-recommendations-list">
                                  {recs.purchek.length > 0 && (
                                    <div className="so-recommendation-card">
                                      <div className="so-recommendation-title-bar">
                                        <span className="so-recommendation-title-text">Purchek Recommended</span>
                                      </div>
                                      <ul className="so-recommendation-reasons-list">
                                        {recs.purchek.map((reason, i) => (
                                          <li key={i}><span className="so-checkmark">&#10003;</span>{reason}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {recs.containment.length > 0 && (
                                    <div className="so-recommendation-card">
                                      <div className="so-recommendation-title-bar">
                                        <span className="so-recommendation-title-text">Cart Containment Recommended</span>
                                      </div>
                                      <ul className="so-recommendation-reasons-list">
                                        {recs.containment.map((reason, i) => (
                                          <li key={i}><span className="so-checkmark">&#10003;</span>{reason}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {recs.cartManager.length > 0 && (
                                    <div className="so-recommendation-card">
                                      <div className="so-recommendation-title-bar">
                                        <span className="so-recommendation-title-text">Cart Manager Recommended</span>
                                      </div>
                                      <ul className="so-recommendation-reasons-list">
                                        {recs.cartManager.map((reason, i) => (
                                          <li key={i}><span className="so-checkmark">&#10003;</span>{reason}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {recs.subscription.length > 0 && (
                                    <div className="so-recommendation-card">
                                      <div className="so-recommendation-title-bar">
                                        <span className="so-recommendation-title-text">Subscription Recommended</span>
                                      </div>
                                      <ul className="so-recommendation-reasons-list">
                                        {recs.subscription.map((reason, i) => (
                                          <li key={i}><span className="so-checkmark">&#10003;</span>{reason}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {recs.pairedWheel.length > 0 && (
                                    <div className="so-recommendation-card">
                                      <div className="so-recommendation-title-bar">
                                        <span className="so-recommendation-title-text">Paired Wheel Recommended</span>
                                      </div>
                                      <ul className="so-recommendation-reasons-list">
                                        {recs.pairedWheel.map((reason, i) => (
                                          <li key={i}><span className="so-checkmark">&#10003;</span>{reason}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="so-no-recommendations">
                                <p>No recommended solution based on current conditions. The store may still be at risk and need protection.</p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
            </>
          )}
        </div>
      </div>

      {showComments && (
        <ListComments
          listName={listName}
          stores={stores.map((s: any) => ({ id: s.id, site: s.brand_name || s.site || s.id, address: s.address || '' }))}
          onClose={() => { setShowComments(false); setOpenCommentStoreId(null); }}
          onCountChange={(total) => {
            setCommentCount(total);
            // Refresh per-store counts
            fetchListComments(listName).then((allComments: any) => {
              const counts = new Map<string, number>();
              allComments.forEach((c: any) => {
                if (c.poi_id) {
                  const prev = counts.get(c.poi_id) || 0;
                  counts.set(c.poi_id, prev + 1 + (c.replies?.length || 0));
                }
              });
              setStoreCommentCounts(counts);
            });
          }}
          initialStoreId={openCommentStoreId}
          readOnly={!canEdit}
        />
      )}

      {showShareModal && (
        <div className="yl-modal-backdrop" onClick={() => setShowShareModal(false)}>
          <div className="yl-modal yl-share-modal" onClick={e => e.stopPropagation()}>
            <div className="yl-modal-header">
              <h2>Share "{listName}"</h2>
              <button className="yl-modal-close" onClick={() => setShowShareModal(false)}>
                <HiX size={18} />
              </button>
            </div>

            <div className="yl-modal-body">
              <div className="yl-form-group">
                <label className="yl-form-label">Add people</label>
                <div className="yl-share-search-row">
                  <div className="yl-share-search-wrap">
                    <input
                      type="text"
                      className="yl-form-input"
                      placeholder="Search by name or email..."
                      value={shareSearch}
                      onChange={e => handleShareSearchChange(e.target.value)}
                      autoFocus
                    />
                    {searchLoading && <span className="yl-share-search-spinner" />}
                  </div>
                  <CustomDropdown
                    className="yl-permission-dropdown"
                    value={shareDefaultPermission}
                    options={[
                      { label: 'View', value: 'view' },
                      { label: 'Edit', value: 'edit' },
                    ]}
                    onChange={val => setShareDefaultPermission(val as 'view' | 'edit')}
                  />
                </div>
                <span className="yl-share-hint">Only @gatekeepersystems.com users</span>

                {shareSuggestions.length > 0 && (
                  <div className="yl-share-suggestions">
                    {shareSuggestions.map(u => (
                      <div key={u.id} className="yl-share-suggestion" onClick={() => handleShareWithUser(u)}>
                        <span className="yl-share-avatar">{(u.name || u.email).charAt(0).toUpperCase()}</span>
                        <div className="yl-share-suggestion-info">
                          <span className="yl-share-suggestion-name">{u.name || u.email.split('@')[0]}</span>
                          <span className="yl-share-suggestion-email">{u.email}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {shareSearch.trim().length >= 2 && !searchLoading && shareSuggestions.length === 0 && (
                  <div className="yl-share-no-results">No matching users found</div>
                )}
              </div>

              <div className="yl-form-group">
                <label className="yl-form-label">People with access</label>
                {shareLoading ? (
                  <div className="yl-share-loading">Loading...</div>
                ) : sharedWith.length === 0 ? (
                  <div className="yl-share-empty">Not shared with anyone yet</div>
                ) : (
                  <div className="yl-share-list">
                    {sharedWith.map(entry => (
                      <div key={entry.id} className="yl-share-entry">
                        <span className="yl-share-avatar">
                          {(entry.shared_with_user.name || entry.shared_with_user.email).charAt(0).toUpperCase()}
                        </span>
                        <div className="yl-share-entry-info">
                          <span className="yl-share-entry-name">
                            {entry.shared_with_user.name || entry.shared_with_user.email.split('@')[0]}
                          </span>
                          <span className="yl-share-entry-email">{entry.shared_with_user.email}</span>
                        </div>
                        <CustomDropdown
                          className="yl-permission-dropdown yl-permission-inline"
                          value={entry.permission || 'view'}
                          options={[
                            { label: 'View', value: 'view' },
                            { label: 'Edit', value: 'edit' },
                          ]}
                          onChange={val => handlePermissionChange(entry.id, val as 'view' | 'edit')}
                        />
                        <button className="yl-share-remove" onClick={() => handleUnshare(entry.id)} title="Remove access">
                          <HiX size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="yl-form-group">
                <label className="yl-form-label">General access</label>
                <div className="yl-org-access-row">
                  <div className="yl-org-access-info">
                    <span className="yl-org-access-icon">
                      <HiOutlineGlobe size={18} />
                    </span>
                    <div className="yl-org-access-text">
                      <span className="yl-org-access-title">
                        {shareOrgAccess ? 'Anyone in the organization' : 'Restricted'}
                      </span>
                      <span className="yl-org-access-desc">
                        {shareOrgAccess
                          ? `Anyone at Gatekeeper Systems can ${shareOrgAccess}`
                          : 'Only people with access can open'}
                      </span>
                    </div>
                  </div>
                  <CustomDropdown
                    className="yl-permission-dropdown"
                    value={shareOrgAccess || 'restricted'}
                    options={[
                      { label: 'Restricted', value: 'restricted' },
                      { label: 'View', value: 'view' },
                      { label: 'Edit', value: 'edit' },
                    ]}
                    onChange={val => handleOrgAccessChange(val === 'restricted' ? null : val as 'view' | 'edit')}
                  />
                </div>
                <div className="yl-copy-link-row">
                  <input
                    className="yl-copy-link-input"
                    readOnly
                    value={`${window.location.origin}/dashboard/list-stores/${listId}`}
                  />
                  <button
                    className="yl-copy-link-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/dashboard/list-stores/${listId}`);
                      toast.success('Link copied to clipboard');
                    }}
                  >
                    Copy link
                  </button>
                </div>
              </div>
            </div>

            <div className="yl-modal-footer">
              <button className="yl-modal-create" onClick={() => setShowShareModal(false)}>Done</button>
            </div>
          </div>
        </div>
      )}

      <BrandNotesDialog
        isOpen={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
        brandName={selectedStoreForNotes}
        isStoreNote={true}
        poiId={selectedStorePoiId}
        notes={stores.find((s: any) => s.id === selectedStorePoiId)?.store_notes}
        onNoteAdded={handleNoteAdded}
      />

      <StoreDetailModal
        isOpen={storeDetailModalOpen}
        store={selectedStoreDetail}
        onClose={() => setStoreDetailModalOpen(false)}
      />
    </div>
  );
}

export default ListStores;
